import { LocalStorageKeys } from '@/constants/LocalStorageKeys';
import CompAddressUtils from '@/models/comp/CompAddressUtils';
import ICompAddress from '@/models/CompAddress';
import ISalesHistory from '@/models/sales-history/ISalesHistory';
import WebEnvConfig from '@/models/WebEnvConfig';
import theLocalStorageUtils from '@/utils/LocalStorageUtils';
import ServiceHelperFactory from './ServiceHelperFactory';
import BaseApiService from '../BaseApiService';
import IServiceHelper from './IServiceHelper';

// Internal use only enums and structures related to building the request.
interface IGetRequestParams {
    address: string;
    city: string;
    state: string;
    zip: string;
}

const productId = `3`; // This is QuickSource

/**
 * Service for getting sales history using the QS API endpoints. Get it from smart data platform (v5/public-records)
 */
export default class SalesHistoryService extends BaseApiService {
    constructor(webEnvConfig: WebEnvConfig) {
        super(webEnvConfig.urls?.quickSourceApi ?? '');
    }

    getServiceHelper(): IServiceHelper {
        return ServiceHelperFactory.getHelper();
    }

    /**
     * Get a public record from the QS public records endpoint (smart data platform) using the given parameters.
     * Required values within the given address are address1, city, state and zip code.
     *
     * @param address
     * @returns a public record or null on error
     */
    async get(address: ICompAddress): Promise<ISalesHistory | null> {
        this.addHeaders({
            'alm-calling-product-name': `${productId}`
        });
        const p2id = theLocalStorageUtils.get(LocalStorageKeys.p2id);
        if (p2id) {
            this.addHeaders({
                'alm-p2id': `${p2id}`
            });
        }
        // this.setLogRequest(true);

        const requestParams: IGetRequestParams = {
            address: address.address1,
            city: address.city ?? '',
            state: address.state ?? '',
            zip: address.zipCode ?? ''
        };

        // Any unit number should be appended to address1 for the QS SDP endpoint
        if (address.unitNumber) {
            requestParams.address += ` ${address.unitNumber}`;
        }

        try {
            const query = this.buildQuery(requestParams);
            const response = await this.axiosInstance.get(`v5/public-records/sales-history${query}`);
            if (response.status === 200) {
                const result: ISalesHistory = response.data;
                return result;
            }
        } catch (error) {
            const errorDescription = this.getErrorDescription(error);
            const addressDescription = CompAddressUtils.getDescription(address);
            console.log(`SalesHistoryService.get(), Error[${errorDescription}] on address[${addressDescription}]`);
        }

        return null;
    }
}
